import {
  type CONSOLE_METHODS,
  METHOD_TO_LEVEL_MAP,
} from '@maestro/core/logger/json-console-logger'
import {
  isSerializedError,
  sanitizeLogs,
} from '@maestro/core/logger/log-sanitizer'
import { safeStringify } from '@maestro/core/logger/utils'

/**
 * Format a log message as JSON for Grafana Loki.
 *
 * Output structure (Option B):
 * - Fixed fields: level, namespace, message, ts
 * - error: First-class Error object (when present)
 * - meta: All other contextual data
 *
 * @example
 * // Standard log
 * {"level":"info","namespace":"maestro:api","message":"Request processed","ts":"...","meta":{"duration":45}}
 *
 * // Error log
 * {"level":"error","namespace":"maestro:auth","message":"Login failed","ts":"...","error":{"name":"AuthError","message":"Invalid token","stack":"..."},"meta":{"userId":"123"}}
 */
export function formatLogMessage({
  method,
  args: rawArgs,
  namespace,
}: {
  method: (typeof CONSOLE_METHODS)[number]
  args: unknown[]
  namespace?: string
}): string {
  // Pass through already-formatted JSON log entries
  if (rawArgs.length === 1 && isJsonLogEntry(rawArgs[0])) {
    return rawArgs[0]
  }

  const args = sanitizeLogs(rawArgs)

  const [message, ...rest] = args

  const output: Record<string, unknown> = {
    level: METHOD_TO_LEVEL_MAP[method],
  }

  if (namespace) {
    output.namespace = namespace
  }

  // Handle message - could be string, Error, or other
  if (isSerializedError(message)) {
    output.message = message.message
    output.error = message
  } else {
    output.message = message
  }

  output.ts = new Date().toISOString()

  // Process remaining args - extract errors to top-level, rest to meta
  let hasError = 'error' in output
  const meta: Record<string, unknown> = {}

  for (const item of rest) {
    if (isSerializedError(item)) {
      if (!hasError) {
        output.error = item
        hasError = true
      } else {
        // Additional errors go to meta
        meta.additionalError = item
      }
    } else if (
      item != null &&
      typeof item === 'object' &&
      !Array.isArray(item)
    ) {
      // Spread object properties into meta, handling nested errors
      for (const [key, value] of Object.entries(item)) {
        if (isSerializedError(value)) {
          if (!hasError) {
            output.error = value
            hasError = true
          } else {
            meta[key] = value
          }
        } else {
          meta[key] = value
        }
      }
    } else if (item !== undefined) {
      // Primitive args go to meta.args array
      if (!meta.args) {
        meta.args = []
      }
      ;(meta.args as unknown[]).push(item)
    }
  }

  if (Object.keys(meta).length > 0) {
    output.meta = meta
  }

  return safeStringify(output)
}

/**
 * Check if a string is already a JSON log entry.
 * Detects output from Winston, writeJsonLog, or fetch-logger.
 */
function isJsonLogEntry(str: unknown): str is string {
  if (typeof str !== 'string') {
    return false
  }
  try {
    const obj = JSON.parse(str)
    return (
      obj &&
      typeof obj === 'object' &&
      'level' in obj &&
      'message' in obj &&
      ('ts' in obj || 'timestamp' in obj)
    )
  } catch {
    return false
  }
}
